import * as XLSX from "xlsx";
import excelConfig from "../components/config/excelConfig.json";

/**
 * Parse Excel into JSON according to configuration
 * @param {File} file - uploaded Excel file
 * @param {Object} overrides - extra options from frontend (ex: { companyAddressUntil: "Balance Sheet" })
 */
export function parseExcel(file, overrides = {}) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false });

      const result = {};

      // merge config with overrides (frontend choice wins)
      const config = { ...excelConfig, ...overrides };

      // ===== 1) Company Name =====
      result.company_name = rows[config.companyNameRow]?.[0] || "";

      // ===== 2) Company Address =====
      let address = [];
      let headerRow = -1;
      for (let r = config.companyAddressStartRow; r < rows.length; r++) {
        const text = (rows[r]?.[0] || "").trim();
        if (text.toLowerCase() === config.companyAddressUntil.toLowerCase()) {
          headerRow = r;
          break;
        }
        if (text) address.push(text);
      }
      result.company_address = address.join(", ");

      // 🚨 Validation: If headerRow not found, return error instead of wrong JSON
      if (headerRow === -1) {
        alert("please select Report type correclty")
        return reject(
          new Error(
            `⚠️ Could not find report type "${config.companyAddressUntil}" in the Excel file. Please select the correct report type and try again.`
          )
        );
      }

      // ===== 3) Period Start & End =====
      let start = "",
        end = "";
      for (let r = headerRow + 1; r < rows.length; r++) {
        const row = rows[r];
        if (!row) continue;

        for (let c = 0; c < row.length; c++) {
          const text = (row[c] || "").trim();
          if (text && /to/i.test(text)) {
            const [s, e] = text.split(/to/i);
            start = s.trim();
            end = e.trim();
            break;
          }
        }
        if (start && end) break;
      }

      result.period_start = start || null;
      result.period_end = end || null;

      // ===== 3.1) Extract Year =====
      let year = null;
      const parseYear = (dateStr) => {
        if (!dateStr) return null;
        const match = dateStr.match(/(\d{2}|\d{4})$/);
        if (match) {
          let yr = parseInt(match[1], 10);
          if (yr < 100) yr = 2000 + yr;
          return yr;
        }
        return null;
      };
      year = parseYear(result.period_end) || parseYear(result.period_start);
      result.year = year;

      // ===== 4) Expenses & Income Parsing =====
      const expenses = {};
      const income = {};
      let expensesTotal = 0;
      let incomeTotal = 0;

      let parentExp = null;
      let parentInc = null;

      for (
        let r = headerRow + config.afterDateNoOfRowSkip;
        r < rows.length;
        r++
      ) {
        const row = rows[r];
        if (!row) continue;

        // ---- Expenses ----
        const leftLabel = row[0];
        const leftChildVal = parseFloat(row[config.childValueColumn]) || null;
        const leftParentVal = parseFloat(row[config.parentValueColumn]) || null;

        if (leftLabel) {
          if (leftLabel.toLowerCase().includes(config.lastRowVal)) {
            if (leftChildVal) expensesTotal = leftChildVal;
            if (leftParentVal) expensesTotal = leftParentVal;
          } else if (leftParentVal) {
            parentExp = { total: leftParentVal, child: [] };
            expenses[leftLabel] = parentExp;
            expensesTotal += leftParentVal;
          } else if (leftChildVal && parentExp) {
            parentExp.child.push({ [leftLabel]: leftChildVal });
          }
        }

        // ---- Income ----
        const rightLabel = row[config.incomeLabelColumn];
        const rightChildVal =
          parseFloat(row[config.incomeChildValueColumn]) || null;
        const rightParentVal =
          parseFloat(row[config.incomeParentValueColumn]) || null;

        if (rightLabel) {
          if (rightLabel.toLowerCase().includes(config.lastRowVal)) {
            if (rightChildVal) incomeTotal = rightChildVal;
            if (rightParentVal) incomeTotal = rightParentVal;
          } else if (rightParentVal) {
            parentInc = { total: rightParentVal, child: [] };
            income[rightLabel] = parentInc;
            incomeTotal += rightParentVal;
          } else if (rightChildVal && parentInc) {
            parentInc.child.push({ [rightLabel]: rightChildVal });
          }
        }
      }

      expenses.total = expensesTotal;
      income.total = incomeTotal;

      result.expenses = expenses;
      result.income = income;

      resolve(result);
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}
